import React, { useState } from 'react';

import LogoIcon from '@/component-library/primitives/Icons/LogoIcon/LogoIcon';

import ComparisonTable from './ComparisonTable';
import CTA from './CTA';
import Footer from './Footer';
import PricingCard from './PricingCard';

import './Pricing.css';

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  const handleBillingToggle = () => {
    setIsAnnual(!isAnnual);
  };

  const pricingPlans = [
    {
      title: 'Starter',
      description: 'For small healthcare practices looking to improve patient engagement',
      monthlyPrice: 149,
      annualPrice: 119,
      features: [
        'Up to 1,000 patient profiles',
        'Basic analytics dashboard',
        'Email & SMS communication',
        'HIPAA-compliant data storage',
        'Standard support (email)',
      ],
      buttonText: 'Start 14-Day Free Trial',
      buttonVariant: 'outline',
      isPopular: false,
    },
    {
      title: 'Professional',
      description: 'For growing practices that need advanced marketing tools',
      monthlyPrice: 299,
      annualPrice: 239,
      features: [
        'Up to 5,000 patient profiles',
        { text: 'Advanced Analytics Dashboard', highlight: 'Full' },
        'AI Campaign Optimization',
        { text: 'Multi-channel Integration', highlight: '15+' },
        'Priority support (email & phone)',
      ],
      buttonText: 'Start 14-Day Free Trial',
      buttonVariant: 'primary',
      isPopular: true,
    },
    {
      title: 'Enterprise',
      description: 'For large healthcare organizations with complex needs',
      price: 'Custom',
      features: [
        'Unlimited patient profiles',
        'Custom analytics & reporting',
        'Advanced AI & automation',
        { text: 'Full multi-channel integration', highlight: '50+' },
        'Dedicated account manager',
      ],
      buttonText: 'Contact Sales',
      buttonVariant: 'outline',
      isPopular: false,
    },
  ];

  const comparisonData = [
    {
      feature: 'Patient Profiles',
      starter: '1,000',
      professional: '5,000',
      enterprise: 'Unlimited',
    },
    {
      feature: 'HIPAA Compliant Storage',
      starter: true,
      professional: true,
      enterprise: true,
    },
    {
      feature: 'Analytics Dashboard',
      starter: 'Basic',
      professional: 'Advanced',
      enterprise: 'Custom',
    },
    {
      feature: 'AI Campaign Optimization',
      starter: false,
      professional: true,
      enterprise: true,
    },
    {
      feature: 'Multi-channel Integration',
      starter: '5 platforms',
      professional: '15+ platforms',
      enterprise: '50+ platforms',
    },
    {
      feature: 'Support',
      starter: 'Email',
      professional: 'Email & Phone',
      enterprise: 'Dedicated Manager',
    },
    {
      feature: 'Custom Reporting',
      starter: false,
      professional: 'Limited',
      enterprise: true,
    },
    {
      feature: 'API Access',
      starter: false,
      professional: true,
      enterprise: true,
    },
  ];

  const faqItems = [
    {
      question: 'How does the 14-day free trial work?',
      answer:
        "Our 14-day free trial gives you full access to the platform with no commitment. You can explore all features of your selected plan, and we don't require a credit card to get started. At the end of your trial, you can choose to subscribe or your account will automatically switch to a limited free version.",
    },
    {
      question: 'Is my healthcare data secure with CyberRaven?',
      answer:
        'Absolutely. We take security seriously and are fully HIPAA-compliant. All patient data is encrypted both in transit and at rest. We implement strict access controls, regular security audits, and maintain comprehensive audit logs to ensure your data remains protected.',
    },
    {
      question: 'Can I upgrade or downgrade my plan later?',
      answer:
        'Yes, you can upgrade your plan at any time, and the new features will be available immediately. If you need to downgrade, changes will take effect at the start of your next billing cycle. Our team can help ensure a smooth transition between plans.',
    },
    {
      question: 'Do you offer custom solutions for large healthcare systems?',
      answer:
        'Yes, our Enterprise plan is specifically designed for large healthcare systems with complex needs. We offer custom integrations, dedicated support, and tailored analytics solutions. Contact our sales team to discuss your specific requirements.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white overflow-hidden relative">
      {/* Background Pattern - Balanced Visibility */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute opacity-10 w-full h-full">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="10" cy="10" r="0.8" fill="rgba(255, 255, 255, 0.7)" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>
      </div>
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${'bg-gray-900/90 backdrop-blur-md py-4 shadow-lg'}`}
      >
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div>
            <LogoIcon withText />
          </div>
          <div className="flex items-center space-x-6">
            <a href="#features" className="hidden md:block hover:text-emerald-300 transition-colors">
              Features
            </a>
            <a href="/pricing" className="hidden md:block hover:text-emerald-300 transition-colors">
              Pricing
            </a>
            <a href="#about" className="hidden md:block hover:text-emerald-300 transition-colors">
              About
            </a>
            <a href="/sign-in" className="text-emerald-300 hover:text-emerald-200 transition-colors">
              Sign in
            </a>
            <a
              href="/contact"
              className="bg-emerald-500 hover:bg-emerald-600 text-gray-900 font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </nav>
      <div className="pricing-page">
        {/* Header would be imported as a separate component */}

        {/* Hero Section */}
        <section className="hero container">
          <h1>
            Transparent <span>Pricing</span> for Healthcare Marketing
          </h1>
          <p>
            Choose the right plan for your healthcare practice and start building stronger patient relationships
            digitally today
          </p>
        </section>

        {/* Toggle Section */}
        <div className="toggle-container container">
          <span className="toggle-option">Monthly</span>
          <label className="toggle-switch">
            <input type="checkbox" id="billing-toggle" checked={isAnnual} onChange={handleBillingToggle} />
            <span className="slider"></span>
          </label>
          <span className="toggle-option">Annual</span>
          <span className="annual-discount">Save 20%</span>
        </div>

        {/* Pricing Cards */}
        <div className="pricing-cards container">
          {pricingPlans.map((plan, index) => (
            <PricingCard
              key={index}
              title={plan.title}
              description={plan.description}
              price={plan.price || (isAnnual ? plan.annualPrice : plan.monthlyPrice)}
              period={plan.price ? null : isAnnual ? 'per month, billed annually' : 'per month'}
              features={plan.features}
              buttonText={plan.buttonText}
              buttonVariant="primary"
              isPopular={plan.isPopular}
            />
          ))}
        </div>

        {/* Compare Plans */}
        <ComparisonTable data={comparisonData} />

        {/* CTA Section */}
        {/* <CTA /> */}

        {/* Footer would be imported as a separate component */}
        <Footer />
      </div>
    </div>
  );
};

export default Pricing;
